import { Link, Stack, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { logout } from '/@/store/session/actions';
import { getAppInstance, getUser } from '/@/store/session/selectors';
import type { FieldviewModalTrackingProps } from '/@/store/tracking/events/user';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { User } from '/@/types/core';
import { formatDate, openWhatsappWeb } from '/@/utils';
import Alert from '../bayer/alert';
import ModalV2 from '../bayer/modal-v2';
import Button from '../button';
import Markdown from '../markdown';

const FIELDVIEW_LOGIN_URL = 'https://climate.com/static/field-view/#/auth/login';
const FIELDVIEW_REGISTER_URL = 'https://climate.com/static/field-view/#/auth/register';

function buildFieldviewModalTrackingProps(user: User): FieldviewModalTrackingProps {
    return {
        user_profile: [user.type, user.subtype].filter((s) => s && String(s).trim()).join(' / ') || 'unknown',
        timestamp: new Date().toISOString(),
        username: user.username,
    };
}

/**
 * Experto FieldView: when `enableFieldviewBlockingModal` and `!validated_type`, blocks post-login navigation
 * (see `App.shouldHoldFieldviewModalAfterLogin`) and shows this modal on the current screen. Closing logs out.
 */
export default function ExpertoFieldViewPostLoginModal() {
    const dispatch = useDispatch();
    const user = useSelector(getUser);
    const appInstance = useSelector(getAppInstance);
    const enableFieldviewBlockingModal = appInstance?.enableFieldviewBlockingModal;

    const handleClose = useCallback(() => {
        dispatch(logout());
    }, [dispatch]);

    const titleMessage = useMemo(() => {
        const appName = settings.app.name;
        if (!appInstance?.fieldviewMigrationDate) return _('{{appName}} pasó a FieldView.', { appName });
        const date = formatDate(new Date(appInstance?.fieldviewMigrationDate), { format: 'long' });
        return _('Desde el {{migrationDate}}, {{appName}} pasó a FieldView.', {
            migrationDate: date,
            appName,
        });
    }, [appInstance?.fieldviewMigrationDate]);

    const cards = useMemo(() => {
        if (!user) {
            return [];
        }
        const base = () => buildFieldviewModalTrackingProps(user);

        return [
            {
                title: _('Ya tengo cuenta en FieldView'),
                description: _('Iniciá sesión con tu usuario existente de FieldView.'),
                button: (
                    <Button
                        variant='contained'
                        color='secondary'
                        sx={{ width: 'fit-content' }}
                        onClick={() => {
                            mpTrackEvent('fieldview_modal_login_clicked', base());
                            window.open(FIELDVIEW_LOGIN_URL);
                        }}
                    >
                        {_('Iniciar sesión')}
                    </Button>
                ),
            },
            {
                title: _('Aún no tengo cuenta en FieldView'),
                description: _('Si todavía no tenés una cuenta, deberás crear una.'),
                alert: (
                    <Alert severity='blue'>
                        <Markdown
                            components={{
                                strong: ({ children }) => (
                                    <Typography variant='body2' color='primary' component='span' fontWeight='bold'>
                                        {children}
                                    </Typography>
                                ),
                                p: ({ children }) => (
                                    <Typography variant='body2' color='primary' component='span'>
                                        {children}
                                    </Typography>
                                ),
                            }}
                        >
                            {_(
                                '**Importante: Usá el mismo correo electrónico con el que te registraste en {{appName}}** para que podamos migrar tu información correctamente.',
                                {
                                    appName: settings.app.name,
                                }
                            )}
                        </Markdown>
                    </Alert>
                ),
                button: (
                    <Button
                        variant='contained'
                        sx={{ width: 'fit-content' }}
                        color='secondary'
                        onClick={() => {
                            mpTrackEvent('fieldview_modal_register_clicked', base());
                            window.open(FIELDVIEW_REGISTER_URL);
                        }}
                    >
                        {_('Crear cuenta gratuita')}
                    </Button>
                ),
            },
        ];
    }, [user]);

    const handleWhatsappClick = useCallback(() => {
        if (!appInstance?.migrationWhatsappContact) return;
        mpTrackEvent('fieldview_modal_whatsapp_clicked', buildFieldviewModalTrackingProps(user));
        openWhatsappWeb({ phone: appInstance.migrationWhatsappContact });
    }, [user, appInstance?.migrationWhatsappContact]);

    const handleInstructivoClick = useCallback(() => {
        if (!appInstance?.fieldviewAccessGuide) return;
        const url = appInstance.fieldviewAccessGuide;
        mpTrackEvent('fieldview_modal_instructivo_clicked', buildFieldviewModalTrackingProps(user));
        window.open(url, '_blank', 'noopener,noreferrer');
    }, [user, appInstance?.fieldviewAccessGuide]);

    if (!enableFieldviewBlockingModal) {
        return null;
    }

    const open = Boolean(user && !user.validated_type);

    const inlineLinkButtonSx = {
        verticalAlign: 'baseline',
        textDecoration: 'underline',
        color: 'secondary.main',
        cursor: 'pointer',
        p: 0,
        border: 0,
        background: 'none',
        font: 'inherit',
    } as const;

    return (
        <ModalV2 open={open} title={titleMessage} onClose={handleClose} maxWidth={'md'}>
            <Stack spacing={2} sx={{ px: 2 }}>
                <Stack direction='column' textAlign='left'>
                    <Markdown>
                        {_(
                            '**Los datos que actualmente almacenas en {{appName}} serán migrados, no perderás información.** Este cambio te permitirá tener una **experiencia más integrada** y eficiente consolidando toda la información y herramientas en un solo lugar.',
                            {
                                appName: settings.app.name,
                            }
                        )}
                    </Markdown>
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ width: '100%' }}>
                    {cards.map((card) => (
                        <Stack
                            key={card.title}
                            direction='column'
                            justifyContent='space-between'
                            spacing={2}
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                border: '1px solid lightgray',
                                borderRadius: '4px',
                                padding: 2,
                                boxSizing: 'border-box',
                                textAlign: 'left',
                            }}
                        >
                            <Stack direction='column' spacing={1}>
                                <Typography variant='h6' color='primary'>
                                    {card.title}
                                </Typography>
                                <Typography variant='body2' color='primary.mui.inactive'>
                                    {card.description}
                                </Typography>
                            </Stack>
                            {card.alert}
                            <Stack>{card.button}</Stack>
                        </Stack>
                    ))}
                </Stack>
                <Typography variant='body2' color='primary' component='p' textAlign='left'>
                    {_('Si tenés dudas o consultas escribínos por ')}
                    <Link component='button' type='button' onClick={handleWhatsappClick} sx={inlineLinkButtonSx}>
                        {_('whatsapp')}
                    </Link>
                    {_(' o consultá el ')}
                    <Link component='button' type='button' onClick={handleInstructivoClick} sx={inlineLinkButtonSx}>
                        {_('instructivo')}
                    </Link>
                </Typography>
            </Stack>
        </ModalV2>
    );
}
